import { useCallback, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import type { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { pushAdoptsSellerModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { STUDIO_DEVIATIONS_STREAM } from '../../../../../constants';
import { getSelectedRowIndex } from '../../../../selectors/studio';

const BATCH_SELL_MAX_ITEMS = 25;

export interface UseBatchSellActionReturn {
  selectedDeviations: any[];
  selectedIds: number[];
  handleClick: () => void;
  handlePartialSelect: (selectionType: 'first' | 'last') => void;
  isOverMaxSellLimit: boolean;
  submenuItems: DropdownOption[] | null;
}

export function useBatchSellAction(): UseBatchSellActionReturn {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    streamConfig
  );
  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const selected = useSelector(getSelectedRowIndex);

  const selectedDeviations = useMemo(
    () => deviations.filter((_deviation, index) => selected.includes(index)),
    [deviations, selected]
  );
  const selectedIds = useMemo(
    () => selectedDeviations.map(obj => obj.deviationId),
    [selectedDeviations]
  );

  const handleClick = useCallback(() => {
    dispatchPushAdoptsSellerModal(selectedDeviations);
  }, [dispatchPushAdoptsSellerModal, selectedDeviations]);

  const handlePartialSelect = useCallback(
    (selectionType: 'first' | 'last') => {
      const partialSelected =
        selectionType === 'first' ? selected.slice(0, 25) : selected.slice(-25);
      dispatchPushAdoptsSellerModal(
        deviations.filter((_deviation, index) =>
          partialSelected.includes(index)
        )
      );
    },
    [dispatchPushAdoptsSellerModal, selected, deviations]
  );

  const isOverMaxSellLimit = selected.length > BATCH_SELL_MAX_ITEMS;

  const submenuItems: DropdownOption[] | null = useMemo(() => {
    if (!hasCoreMembership || !isOverMaxSellLimit) {
      return null;
    }

    return [
      {
        label: (
          <Trans i18nKey="studio.table.header.sell.tooltip">
            You can sell a maximum
            <br />
            of 25 items at once
          </Trans>
        ),
        disabled: true,
      },
      {
        label: t('studio.table.header.sell.option.first25'),
        onClick: () => handlePartialSelect('first'),
      },
      {
        label: t('studio.table.header.sell.option.last25'),
        onClick: () => handlePartialSelect('last'),
      },
    ];
  }, [hasCoreMembership, isOverMaxSellLimit, t, handlePartialSelect]);

  return {
    selectedDeviations,
    selectedIds,
    handleClick,
    handlePartialSelect,
    isOverMaxSellLimit,
    submenuItems,
  };
}
